<script>
  import { afterUpdate, getContext, onDestroy, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Link, link, useNavigate } from 'svelte-navigator';
  import {
    Breadcrumb,
    BreadcrumbItem,
    Button,
    Col,
    Container,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Row,
    Tooltip
  } from '@sveltestrap/sveltestrap';

  import { CampaignCard } from '@client/components/campaign-card';
  import { Capability } from '@client/components/capability';
  import { EmptyState } from '@client/components/empty-state';
  import { FilterBar } from '@client/components/filter-bar';
  import { DateFilter } from '@client/components/filters/date';
  import { MultiSelectFilter } from '@client/components/filters/multiselect';
  import { SearchFilter } from '@client/components/filters/search';
  import { SortFilter } from '@client/components/filters/sort';
  import { Icon } from '@client/components/icon';
  import { LayoutControls } from '@client/components/layout-controls';
  import { Loader } from '@client/components/loader';
  import { NoMatches } from '@client/components/no-matches';
  import { getNotificationsContext } from '@client/components/notifications';
  import { Pagination } from '@client/components/pagination';
  import { RenameModal } from '@client/components/rename-modal';
  import { Spinner } from '@client/components/spinner';
  import { Table } from '@client/components/table';
  import { Tour } from '@client/components/tour';

  import { AdsService } from '@client/services/ads';
  import { AssetsService } from '@client/services/assets';
  import { CampaignsService } from '@client/services/campaigns';
  import { PluginsService } from '@client/services/plugins';
  import { TargetingService } from '@client/services/targeting';

  import { appStore } from '@client/stores/app';
  import { activeDomain, internalDomains } from '@client/stores/domain-settings';
  import { campaignsSettingsStore } from '@client/stores/page-settings';

  import { isInternalRecipient } from '@client/utils/activity';
  import { consolidateMetrics, formatExportRate } from '@client/utils/banners';
  import { determineCampaignStatus } from '@client/utils/campaigns';
  import { METRICS_REQUEST_CONCURRENCY, resolveMetricsWindow } from '@client/utils/campaigns';
  import { buildExportExtensionCells, NEW_EXPORT_HEADER_KEYS } from '@client/utils/campaigns';
  import { csvField, downloadFile } from '@client/utils/csv';
  import { structureTree } from '@client/utils/folder-tree';
  import { QueryParams } from '@client/utils/query-params';
  import { setTourStoreSettings } from '@client/utils/tours';

  import { CAMPAIGN_FILTERS } from '@client/enums';

  import { deepCopyArray, sortCollection } from '@common/utils/arrays';
  import { generatePastelColors } from '@common/utils/colors';
  import { formatDate } from '@common/utils/dates';
  import { debounce } from '@common/utils/debounce';
  import { deepCopy, merge } from '@common/utils/objects';
  import { mapWithConcurrency } from '@common/utils/promise';

  import { CLONE_STEPS } from './tours/clone';
  import { CREATE_STEPS } from './tours/create';
  import { EDIT_STEPS } from './tours/edit';
  import { FULL_STEPS } from './tours/full';
  import { OVERVIEW_STEPS } from './tours/overview';
  import { ActionsCell, BannerCell, CampaignCell, PerformanceCell, StatusCell } from './cell-formatters';
  import { FoldersFilter } from './folder-filter';
  import { AdModal, CampaignModal, TargetingModal } from './modals';

  const { basepath } = getContext('config');

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const navigate = useNavigate();
  const redirect = (path) => navigate(path);

  let showFilters = true;
  let showCampaignCreationModal = false;
  let showRenameModal = false;
  let renameCampaign = null;
  let creatingNewCampaign = false;
  let showAdSetupModal = false;
  let showTargetingModal = false;
  let modalData = {};
  let totalRecords = 0;
  let queryResult = [];
  let targeting = {};
  let integrations = [];
  let otherCampaigns = [];
  let records = [];
  let filtered = [];
  let folders = [];
  let loaded = false;
  let updating = false;
  let cache = {};
  let sortDirection = 1;
  let sortKey = 'activeStatus';
  let sortDir = sortDirection === -1 ? 'asc' : 'desc';
  let tagFilters = {};
  let categoryFilters = {};

  let selectedFolders = [];

  let assignedTagColors = {};
  let tagColors = generatePastelColors(400);
  let assignedCategoryColors = {};
  let categoryColors = generatePastelColors(400);

  let originalRecordCount = 0;
  let initialized = false;
  let inflightRequests = [];

  let isOpen = false;

  let filters = merge({}, CAMPAIGN_FILTERS, $campaignsSettingsStore.filters);
  let previousSearch = '';

  filters.search = QueryParams.get('search') || filters.search;
  filters.tags = QueryParams.get('tags') || filters.tags;
  filters.categories = QueryParams.get('categories') || filters.categories;
  filters.folders = QueryParams.get('folders') || filters.folders;
  filters.dateRange.startDate = QueryParams.get('startDate') || filters.dateRange.startDate || '';
  filters.dateRange.endDate = QueryParams.get('endDate') || filters.dateRange.endDate || '';
  filters.sorting.key = QueryParams.get('sortKey') || filters.sorting.key || sortKey;
  filters.sorting.dir = QueryParams.get('sortDir') || filters.sorting.dir || sortDir;
  (filters.paging.perPage = QueryParams.get('perpage') || filters.paging.perPage),
    (filters.paging.page = QueryParams.get('page') || filters.paging.page || 1),
    (sortKey = filters.sorting.key);
  sortDir = filters.sorting.dir;
  sortDirection = filters.sorting.dir === 'asc' ? -1 : 1;

  let startDate = filters.dateRange.startDate;
  let endDate = filters?.dateRange.endDate;
  let renderCardLayout = $appStore.mobile || filters?.layout === 'card';

  let windowWidth = window.innerWidth;
  let exporting = false;

  let statusFilters = {
    'active-scheduled': {
      label: 'label.ACTIVE',
      value: 'active-scheduled',
      enabled: false
    },
    'active-targeting': {
      label: 'label.ACTIVE',
      value: 'active-targeting',
      enabled: false
    },
    expired: {
      label: 'label.CAMPAIGN_EXPIRED',
      value: 'expired',
      enabled: false
    },
    inactive: {
      label: 'label.INACTIVE',
      value: 'inactive',
      enabled: false
    },
    paused: {
      label: 'label.PAUSED',
      value: 'paused',
      enabled: false
    },
    upcoming: {
      label: 'label.CAMPAIGN_UPCOMING',
      value: 'upcoming',
      enabled: false
    }
  };

  const sortOptions = [
    { key: 'creationDate', label: 'label.CREATION_DATE', value: 'creationDate' },
    { key: 'rulename', label: 'label.NAME', value: 'rulename' },
    { key: 'scheduled', label: 'label.SCHEDULE_DATE', value: 'scheduled' },
    { key: 'activeStatus', label: 'label.STATUS', value: 'activeStatus' }
  ];

  let sortFilters = sortOptions.reduce((acc, { key, label, value }) => {
    acc[key.toLowerCase()] = {
      label,
      value,
      enabled: sortKey === value
    };
    return acc;
  }, {});

  const createCategoryFilters = (data) => {
    const selected = filters.categories.split(',');

    const definitions = data.reduce((acc, { category }) => {
      if (!category) {
        return acc;
      }

      const categories = Object.keys(category);

      if (categories.length) {
        categories.forEach((label) => {
          if (!acc[label]) {
            acc[label] = {
              label,
              value: label,
              enabled: selected.includes(label)
            };
          }
        });
      }

      return acc;
    }, {});

    return Object.keys(definitions)
      .sort()
      .reduce((acc, key) => (acc[key] = definitions[key]) && acc, {});
  };

  const createTagFilters = (data) => {
    const selected = filters.tags.split(',');

    const definitions = data.reduce((acc, { tags }) => {
      if (tags.length) {
        tags.forEach(({ text }) => {
          if (!acc[text]) {
            acc[text] = {
              label: text,
              value: text,
              enabled: selected.includes(text)
            };
          }
        });
      }

      return acc;
    }, {});

    return Object.keys(definitions)
      .sort()
      .reduce((acc, key) => (acc[key] = definitions[key]) && acc, {});
  };

  const hasFilesAtRoot = (folders) => {
    const rootFolder = folders.find((folder) => folder.parent === '#');
    return !!rootFolder?.files?.length;
  };

  const trackRequest = (request, label) => {
    if (request?.cancel) {
      inflightRequests = [...inflightRequests, { request, label }];
    }

    return request;
  };

  const cancelInflightRequests = (reason) => {
    if (!inflightRequests.length) {
      return;
    }

    inflightRequests.forEach(({ request, label }) => {
      if (request?.cancel) {
        request.cancel();
        console.info('Request cancelled', { label, reason, route: 'marketing/campaigns' });
      }
    });

    inflightRequests = [];
  };

  const createFolderTree = async () => {
    const responseRequest = trackRequest(AssetsService.getAssetFolders(), 'assets:getAssetFolders');
    const response = await responseRequest;

    if (!Array.isArray(response) || !response.length) {
      folders = [];
      return;
    }

    folders = [structureTree(response)];
  };

  const onSearchFilterChange = debounce(() => {
    update(true);
  }, 250);

  const onSortChange = (selected, key, dir) => {
    sortKey = key;
    sortDirection = dir === 'asc' ? -1 : 1;
    sortDir = dir;
    filters.sorting.key = key;
    filters.sorting.dir = dir;

    update(true);
  };

  const onSort = (key, dir) => {
    sortKey = key;
    sortDirection = dir;
    sortDir = dir === -1 ? 'asc' : 'desc';
    filters.sorting.key = key;
    filters.sorting.dir = sortDir;
    QueryParams.set('sortDir', sortDir);
    QueryParams.set('sortKey', key);
    update(true);
  };

  const onChangePage = (page) => {
    filters.paging.page = page;
    update(true);
  };

  const onChangeRecords = (perPage) => {
    filters.paging.perPage = perPage;
    update(true);
  };

  const onNewCampaign = () => {
    creatingNewCampaign = false;
    creatingNewCampaign = true;
    showCampaignCreationModal = true;
  };

  const onExportCampaigns = async (isAll) => {
    $appStore.loading = true;
    exporting = true;

    const items = isAll ? queryResult : exportRecords;
    const exportRange = !isAll && startDate && endDate ? { start: startDate, end: endDate } : null;

    const metrics = await mapWithConcurrency(
      items,
      (campaign) => getMetrics(campaign, true, exportRange),
      METRICS_REQUEST_CONCURRENCY
    );

    exporting = false;
    $appStore.loading = false;

    const headers = [
      $format('label.REPORTING_PERIOD'),
      $format('label.CAMPAIGN'),
      $format('label.DAYS_LIVE'),
      $format('label.TYPE'),
      $format('label.DELIVERIES'),
      $format('label.IMPRESSIONS'),
      $format('label.CLICKS'),
      $format('label.REACH'),
      $format('label.CLICK_THROUGH_RATE'),
      $format('label.CLICK_TO_RECIPIENT_RATE'),
      $format('label.TAGS'),
      $format('label.BASIC_VIEWS'),
      $format('label.BASIC_CLICKS'),
      $format('label.BASIC_CLICK_THROUGH_RATE'),
      $format('label.URL'),
      ...NEW_EXPORT_HEADER_KEYS.map((key) => $format(key)),
      $format('label.TOTAL_IMPRESSIONS'),
      $format('label.TOTAL_CLICKS'),
      $format('label.TOTAL_CTR')
    ];

    const reportingPeriod =
      !isAll && startDate && endDate
        ? formatDate(startDate, 'MM/dd/yyyy') + '-' + formatDate(endDate, 'MM/dd/yyyy')
        : 'all-time';

    const rows = metrics.map((entry, index) => {
      return [
        reportingPeriod,
        entry.name,
        entry.totals.dayslive,
        entry.type,
        entry?.totals?.ad || 0,
        entry?.totals?.adview || 0,
        entry?.totals?.adclick || 0,
        entry?.totals?.uniques || 0,
        formatExportRate(entry?.totals?.ctr),
        formatExportRate(entry?.totals?.ctrr),
        entry.tags.map((tag) => tag.text).join('; '),
        entry?.totals?.basicadview || 0,
        entry?.totals?.basicadclick || 0,
        formatExportRate(entry?.totals?.basicCtr),
        items[index].previewURL || '',
        ...buildExportExtensionCells(items[index], $format),
        entry?.totals?.combinedViews || 0,
        entry?.totals?.combinedClicks || 0,
        formatExportRate(entry?.totals?.combinedCtr)
      ];
    });

    const csvContent = [headers, ...rows].map((e) => e.map(csvField).join(',')).join('\n');
    const filename = isAll ? 'campaigns-report' : `campaigns-report(${items.length})`;
    const range = isAll ? 'all-time' : formatDate(startDate, 'MM/dd/yyyy') + '-' + formatDate(endDate, 'MM/dd/yyyy');

    downloadFile(csvContent, `${filename}-${range}-${Date.now()}`);
  };

  const onExportClicks = async (isAll) => {
    $appStore.loading = true;
    exporting = true;

    const items = isAll ? queryResult : exportRecords;
    const history = await mapWithConcurrency(
      items,
      (campaign) => AdsService.getClickReport(campaign._id, 365),
      METRICS_REQUEST_CONCURRENCY
    );

    exporting = false;
    $appStore.loading = false;

    const headers = [
      $format('label.TIMESTAMP'),
      $format('label.CAMPAIGN'),
      $format('label.URL'),
      $format('label.SENDER'),
      $format('label.RECIPIENT'),
      $format('label.DOMAIN'),
      'ISP'
    ];

    let rows = [];

    Object.keys(history).forEach((campaignKey) => {
      const entries = history[campaignKey];
      const sourceCampaign = items[campaignKey];

      // When "Allow Internal" is off for a campaign, drop its internal recipients so the export
      // matches the performance metrics (which exclude internal events server-side).
      const visibleEntries = sourceCampaign?.countinternal
        ? entries
        : entries.filter((entry) => !isInternalRecipient(entry.recipient, $activeDomain, $internalDomains));

      const campaignRows = visibleEntries.map((entry) => [
        new Date(entry.utcts),
        entry.note.name,
        entry.note.url,
        entry.sender,
        entry.recipient,
        entry.recipient.split('@')[1],
        entry?.note?.isp?.replace(/,/g, ' ') || ''
      ]);

      rows = rows.concat(campaignRows);
    });

    const csvContent = [headers, ...rows].map((e) => e.join(',')).join('\n');
    const filename = isAll ? 'clicks-report' : `clicks-report(${items.length})`;
    const range = isAll ? 'all-time' : formatDate(startDate, 'MM/dd/yyyy') + '-' + formatDate(endDate, 'MM/dd/yyyy');

    downloadFile(csvContent, `${filename}-${range}-${Date.now()}`);
  };

  const getMetrics = async (campaign, isExport, range) => {
    const id = campaign._id;
    const cacheKey = range ? `${id}-${range.start}-${range.end}` : id;

    if (!cache[cacheKey]) {
      // A ranged export filters to the range just below, so only fetch far enough back to
      // cover it. The all-campaigns export has no range and genuinely wants the lifetime.
      const { days } = resolveMetricsWindow({
        campaign,
        fullHistory: !range,
        startDate: range?.start || startDate
      });
      const data = await CampaignsService.getMetrics({ aid: campaign._id, days });

      let filteredData = data;
      if (range?.start && range?.end) {
        const start = new Date(range.start);
        const end = new Date(range.end);
        filteredData = data.filter((item) => {
          const ts = new Date(item.timeslice);
          return ts >= start && ts <= end;
        });
      }

      cache[cacheKey] = {
        metrics: consolidateMetrics(filteredData, campaign)
      };
    }

    if (isExport) {
      cache[cacheKey].metrics = {
        ...cache[cacheKey].metrics,
        name: campaign.rulename,
        tags: campaign.tags,
        type: campaign.adtype
      };
    }

    return cache[cacheKey].metrics;
  };

  const onTagsFilterChange = (e, value, filter, selected) => {
    filters.tags = selected.join(',');
    update(true);
  };

  const onCategoryFilterChange = (e, value, filter, selected) => {
    filters.categories = selected.join(',');
    update(true);
  };

  const onFolderFilterChange = (e, selected) => {
    selectedFolders = selected;
    filters.folders = selected.join(',');
    update(true);
  };

  const onDateChange = (dateRange) => {
    if ((dateRange.startDate && dateRange.endDate) || (!dateRange.startDate && !dateRange.endDate)) {
      filters.dateRange.startDate = dateRange.startDate;
      filters.dateRange.endDate = dateRange.endDate;
      update(true);
    }
  };

  const onUpdateLayout = (showCard) => {
    renderCardLayout = !!showCard;
    filters.layout = renderCardLayout ? 'card' : 'table';

    $campaignsSettingsStore.filters.layout = filters.layout;
  };

  const onActionUpdate = async (campaignObject) => {
    const currentIndex = filtered.findIndex(({ _id }) => _id === campaignObject._id);
    campaignObject = determineCampaignStatus(campaignObject, targeting, integrations);
    filtered.splice(currentIndex, 1, campaignObject);
    filtered = [...filtered];
  };

  const onActionDelete = async (campaignObject) => {
    const currentIndex = filtered.findIndex(({ _id }) => _id === campaignObject._id);
    filtered.splice(currentIndex, 1);
    filtered = [...filtered];

    if (!filtered.length) {
      originalRecordCount = 0;
    }
  };

  const onRenameUpdate = async (campaign, newName) => {
    const response = await CampaignsService.updateCampaign({ ...campaign, rulename: newName });

    if (response?.status >= 400) {
      return notify($format('label.FAILURE.CAMPAIGN_RENAME', { values: { rulename: campaign.rulename } }), 'error');
    }

    notify($format('label.SUCCESS.RENAME', { values: { from: campaign.rulename, to: newName } }));

    showRenameModal = false;
    update();
  };

  const assignActiveStatus = (record) => {
    const { active, completed, notscheduled, paused, schedule, scheduled, rulename, targetingSources } = record;
    const upcoming = new Date(schedule?.startDate).getTime() >= new Date().getTime();

    if (paused) {
      return `paused-${rulename}`;
    }

    if (active && !upcoming) {
      return `active-scheduled-${rulename}`;
    }

    if (!!notscheduled && !!targetingSources) {
      return `active-targeting-${rulename}`;
    }

    if (active && upcoming) {
      return `upcoming-${rulename}`;
    }

    if (completed && !!scheduled) {
      return `expired-${rulename}`;
    }

    return `inactive-${rulename}`;
  };

  let exportRecords = [];

  const update = async (skip) => {
    cancelInflightRequests('update-restart');
    updating = true;

    try {
      if (!skip) {
        otherCampaigns = [];

        await createFolderTree();

        const campaignsRequest = trackRequest(CampaignsService.getAll(), 'campaigns:getAll');
        queryResult = await campaignsRequest;

        if (!Array.isArray(queryResult)) {
          updating = false;
          inflightRequests = [];
          return;
        }

        if (!initialized) {
          originalRecordCount = queryResult.length;
        }

        tagFilters = await createTagFilters(queryResult);
        categoryFilters = await createCategoryFilters(queryResult);

        queryResult = queryResult.map((item, index) => {
          item.order = index;

          item = determineCampaignStatus(item, targeting, integrations);

          item.activeStatus = assignActiveStatus(item);

          item.metrics = {};

          if (item.tags.length) {
            item.tags = item.tags
              .map((tag) => {
                if (!assignedTagColors[tag.text]) {
                  assignedTagColors[tag.text] = tagColors.shift();
                }

                return {
                  ...tag,
                  backgroundColor: assignedTagColors[tag.text]
                };
              })
              .sort((a, b) => a.text.localeCompare(b.text));
          }

          if (item.category) {
            item.categories = Object.keys(item.category).map((category) => {
              if (!assignedCategoryColors[category]) {
                assignedCategoryColors[category] = categoryColors.shift();
              }

              return {
                text: category,
                backgroundColor: assignedCategoryColors[category]
              };
            });
          }

          otherCampaigns.push(item);
          return item;
        });
      }

      queryResult = [...otherCampaigns];

      records = deepCopy(queryResult);

      const selectedStatus = Object.values(statusFilters)
        .map(({ enabled, value }) => (enabled ? value : false))
        .filter(Boolean);

      if (selectedStatus.length) {
        records = records.filter(({ activeStatus }) => {
          const statusType = activeStatus.split('-').slice(0, -1).join('-');
          return selectedStatus.includes(statusType);
        });
      }

      if (filters.search.length) {
        records = records.filter(
          (item) =>
            JSON.stringify(Object.values(item).join(',')).toLowerCase().indexOf(filters.search.toLowerCase()) > -1
        );
      }

      if (previousSearch !== filters.search) {
        filters.paging.page = 1;
      }

      previousSearch = filters.search || '';

      if (startDate && endDate) {
        records = records.filter(({ creationDate, scheduled, schedule, enableschedule, rulename, notscheduled }) => {
          const start = new Date(startDate).setHours(0, 0, 0, 0);
          const end = new Date(endDate).setHours(23, 59, 59, 999);

          // Only apply the schedule date-range test when scheduling is actually enabled.
          if (enableschedule && schedule) {
            return new Date(schedule.startDate).getTime() <= end && new Date(schedule.endDate).getTime() >= start;
          }

          // Banners with scheduling disabled (enableschedule === false) aren't bound to a date range,
          // even if a stale schedule object lingers. notscheduled already === !enableschedule.
          if (notscheduled) {
            return true;
          }

          return false;
        });
      }

      if (Object.keys(tagFilters).filter((item) => tagFilters[item].enabled).length) {
        const selectedTags = Object.keys(tagFilters)
          .map((item) => (tagFilters[item].enabled ? item : false))
          .filter(Boolean);

        records = records.filter(({ tags }) => {
          let contains = false;

          for (let i = 0, k = tags.length; i < k; i++) {
            const item = tags[i];

            if (selectedTags.includes(item.text)) {
              contains = true;
              break;
            }
          }

          return contains;
        });
      }

      if (Object.keys(categoryFilters).filter((item) => categoryFilters[item].enabled).length) {
        const selectedCategories = Object.keys(categoryFilters)
          .map((item) => (categoryFilters[item].enabled ? item : false))
          .filter(Boolean);

        records = records.filter(({ category: categories }) => {
          let contains = false;

          for (let category in categories) {
            if (selectedCategories.includes(category)) {
              contains = true;
              break;
            }
          }

          return contains;
        });
      }

      if (selectedFolders?.length) {
        const selectedFolderIds = selectedFolders.map((folder) => folder.split('-')[1]);

        records = records.filter(({ folderid }) => {
          return selectedFolderIds.includes(folderid);
        });
      }

      const recordCount = records.length;

      const hasFilters =
        (startDate && endDate) ||
        includesCategoryFilters ||
        includesFolderFilters ||
        includesTagFilters ||
        filters.search.length;

      if (filters.paging.page === 0) {
        filters.paging.page = 1;
      }

      exportRecords = deepCopyArray(records);

      records = sortCollection(records, sortKey, sortDirection).slice(
        (filters.paging.page - 1) * filters.paging.perPage,
        filters.paging.page * filters.paging.perPage
      );

      filtered = [...records];

      totalRecords = hasFilters ? recordCount : queryResult.length;

      $campaignsSettingsStore.filters = { ...filters };

      loaded = true;
      updating = false;
      initialized = true;
      inflightRequests = [];
    } catch (error) {
      if (error?.message === 'Canceled' || error?.__CANCEL__) {
        updating = false;
        inflightRequests = [];
        return;
      }

      updating = false;
      throw error;
    }
  };

  let columns = [
    {
      key: 'name',
      title: $format('label.CREATIVE'),
      value: ({ rulename }) => rulename,
      renderComponent: BannerCell,
      headerClass: 'banner-cell',
      sortable: false
    },
    {
      key: 'creationDate',
      title: $format('label.CAMPAIGN'),
      headerClass: 'campaign-cell',
      value: ({ creationDate }) => creationDate,
      renderComponent: CampaignCell,
      sortable: true
    },
    {
      key: 'metrics',
      title: $format('label.PERFORMANCE'),
      tooltip: $format('label.TOOLTIP.CAMPAIGN_CHART_RANGE'),
      headerClass: 'metrics-cell',
      sortable: false,
      renderComponent: PerformanceCell
    },
    {
      key: 'activeStatus',
      title: $format('label.STATUS'),
      headerClass: 'status-cell',
      value: ({ activeStatus }) => activeStatus,
      renderComponent: StatusCell,
      sortable: true
    },
    {
      key: 'actions',
      title: '',
      value: '',
      headerClass: 'actions-cell',
      renderComponent: {
        component: ActionsCell,
        props: {
          update: onActionUpdate,
          delete: onActionDelete,
          onRename: (row) => {
            renameCampaign = row;
            showRenameModal = true;
          }
        }
      }
    }
  ];

  const onResize = debounce(() => {
    windowWidth = window.innerWidth;
  }, 150);

  onMount(async () => {
    updating = true;

    targeting = await TargetingService.get();
    integrations = await PluginsService.getAll();

    updating = false;

    await update();
  });

  const onStatusFilterChange = () => {
    update(true);
  };

  afterUpdate(() => {
    $campaignsSettingsStore = setTourStoreSettings($campaignsSettingsStore, !!records.length);
  });

  onDestroy(() => cancelInflightRequests('component-destroyed'));

  $: hasData = $campaignsSettingsStore?.tours?.hasData;

  $: campaignsTourSteps = [
    {
      steps: OVERVIEW_STEPS,
      stepsKey: 'overview',
      menuLabel: $format('label.TOURS.CAMPAIGNS.MENU.OVERVIEW'),
      storeKey: 'overviewHasToured',
      disabled: !hasData
    },
    {
      steps: CREATE_STEPS,
      stepsKey: 'default',
      menuLabel: $format('label.TOURS.CAMPAIGNS.MENU.CREATE'),
      storeKey: 'createHasToured',
      disabled: false
    },
    {
      steps: CLONE_STEPS,
      stepsKey: 'clone',
      menuLabel: $format('label.TOURS.CAMPAIGNS.MENU.CLONE'),
      storeKey: 'cloneHasToured',
      disabled: !hasData
    },
    {
      steps: EDIT_STEPS,
      stepsKey: 'edit',
      menuLabel: $format('label.TOURS.CAMPAIGNS.MENU.EDIT'),
      storeKey: 'editHasToured',
      disabled: !hasData
    },
    {
      steps: FULL_STEPS,
      stepsKey: 'full'
    }
  ];

  let content = null;

  const generatePDF = () => {
    const element = content;
    const options = {
      margin: 0.1,
      filename: 'campaigns.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' }
    };

    window.html2pdf().from(element).set(options).save();
  };

  $: dismissedOrToured = $campaignsSettingsStore?.tours?.isDismissed || $campaignsSettingsStore?.tours?.hasToured;
  $: hasCampaigns = initialized && originalRecordCount > 0;
  $: tabletView = windowWidth <= 905;
  $: includesTagFilters = Object.keys(tagFilters).length;
  $: includesCategoryFilters = Object.keys(categoryFilters).length;
  $: includesFolderFilters = folders.length && hasFilesAtRoot(folders);
  $: if (initialized && startDate && endDate) {
    update(true);
  }

  $: hasFiltersSelected = !!(
    filters.categories.length ||
    (filters.dateRange.startDate && filters.dateRange.endDate) ||
    filters.folders.length ||
    filters.search.length ||
    filters.tags.length
  );
</script>

<div class="page manage-campaigns" bind:this={content}>
  <Loader backdrop show={!loaded || updating} />

  <div class="page-header no-bottom-border">
    <Container fluid>
      <Row class="flex-align--center">
        <Col>
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/marketing/campaigns" use:link>{$format('label.MARKETING')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{$format('label.CAMPAIGNS')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center">
        <Col xs="6" sm="6" md="6">
          <h1 class="page-title">{$format('label.CAMPAIGNS')}</h1>
        </Col>
        <Col xs="6" sm="6" md="6" class="text-right">
          <Capability action="manage" subject="staff">
            {#if dismissedOrToured && hasCampaigns}
              <Tour steps={campaignsTourSteps} store={campaignsSettingsStore} />
            {/if}
          </Capability>
          <Button id="new-campaign-btn" color="secondary" on:click={onNewCampaign}>
            {$format('label.NEW_CAMPAIGN')}
          </Button>
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    <div class="tour-wrapper"></div>
    <div class="toolbar">
      <div class="tabs">
        <div class="tab-list">
          <Link class="tab active" to="/marketing/campaigns">{$format('label.CAMPAIGNS')}</Link>
          <Link class="tab" to="/marketing/campaigns/editorial">
            {$format('label.EDITORIAL')} <span class="hide--mobile">&nbsp;{$format('label.CALENDAR')}</span>
          </Link>
        </div>
      </div>
      <Capability action="manage" subject="staff">
        {#if !dismissedOrToured && hasCampaigns}
          <Tour
            steps={campaignsTourSteps}
            store={campaignsSettingsStore}
            tourHeader={$format('label.TOURS.CAMPAIGNS.ALERT.HEADER')}
            tourDescription={$format('label.TOURS.CAMPAIGNS.ALERT.DESCRIPTION')}
            marginBottom="mb-2"
          />
        {/if}
      </Capability>
      {#if hasCampaigns}
        <div class="filters">
          <FilterBar bind:show={showFilters}>
            <SearchFilter bind:search={filters.search} onChange={onSearchFilterChange} />

            <MultiSelectFilter
              bind:filter={statusFilters}
              onClick={onStatusFilterChange}
              param="status"
              label={$format('label.STATUS')}
              maxHeight="400px"
            >
              <span class="badge-{config.value}" slot="label" let:config>
                {$format(config.label)}
              </span>
            </MultiSelectFilter>

            {#if includesTagFilters}
              <MultiSelectFilter
                bind:filter={tagFilters}
                onClick={onTagsFilterChange}
                search={true}
                param="tags"
                label={$format('label.TAGS')}
                maxHeight="400px"
              />
            {/if}

            {#if includesCategoryFilters}
              <MultiSelectFilter
                bind:filter={categoryFilters}
                onClick={onCategoryFilterChange}
                search={true}
                param="categories"
                label={$format('label.CATEGORIES')}
                maxHeight="400px"
              />
            {/if}

            {#if includesFolderFilters}
              <FoldersFilter bind:folders onClick={onFolderFilterChange} />
            {/if}

            {#if $appStore.mobile || renderCardLayout || tabletView}
              <SortFilter
                bind:filter={sortFilters}
                bind:sortDirection={sortDir}
                onClick={onSortChange}
                sortDirParam="sortDir"
                sortKeyParam="sortKey"
              />
            {/if}
            <DateFilter isRange isMultipane bind:startDate bind:endDate bind:isOpen {onDateChange} />
          </FilterBar>
          <Dropdown id="export-dropdown">
            <DropdownToggle size="sm" class="export-dropdown" disabled={!filtered.length || exporting}>
              {#if exporting}
                <Spinner styles="border-width: 2px; color: #0087ff; height: 14px; width: 14px;" show inline />
              {:else}
                <Icon name="download" />
                <span class="visually-hidden">{$format('label.EXPORT')}</span>
              {/if}
            </DropdownToggle>
            <DropdownMenu end>
              <!-- <Capability action="manage" subject="staff">
                <DropdownItem on:click={generatePDF}>Download as PDF</DropdownItem>
                <DropdownItem divider />
              </Capability> -->
              {#if hasFiltersSelected}
                <DropdownItem on:click={() => onExportCampaigns()}>
                  {@html $format('label.EXPORT_X_TYPE', {
                    values: {
                      total: Number(totalRecords).toLocaleString(),
                      type: $format('label.CAMPAIGNS')
                    }
                  })}
                </DropdownItem>
                <DropdownItem on:click={() => onExportClicks()}>
                  {@html $format('label.EXPORT_CAMPAIGN_CLICKS', {
                    values: {
                      total: Number(totalRecords).toLocaleString()
                    }
                  })}
                </DropdownItem>
                <DropdownItem divider />
              {/if}
              <DropdownItem on:click={() => onExportCampaigns(true)}>
                {@html $format('label.EXPORT_ALL_TYPE', {
                  values: { type: $format('label.CAMPAIGNS') }
                })}
              </DropdownItem>
              <DropdownItem on:click={() => onExportClicks(true)}>
                {@html $format('label.EXPORT_ALL_TYPE', {
                  values: { type: $format('label.CLICKS') }
                })}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
          <Tooltip target="export-dropdown">
            {$format('label.EXPORT')}
          </Tooltip>

          <LayoutControls {renderCardLayout} onChange={onUpdateLayout} />
        </div>
      {/if}
    </div>
    <Container fluid>
      <Row>
        {#if hasCampaigns}
          {#if filtered.length}
            {#if $appStore.mobile || renderCardLayout || tabletView}
              {#each filtered as campaign, index}
                <Col xs="12" sm="12" md="6" lg="6" xl="4" xxl="3">
                  <CampaignCard
                    {campaign}
                    {cache}
                    {onActionUpdate}
                    onRename={(campaign) => {
                      renameCampaign = campaign;
                      showRenameModal = true;
                    }}
                  />
                </Col>
              {/each}
            {:else}
              <Col>
                <Table
                  id="campaigns"
                  classNameThead="sticky-header"
                  emptyLabel={$format('label.MESSAGE.NO_MATCHES_CAMPAIGNS')}
                  bind:rows={filtered}
                  bind:columns
                  bind:sortBy={sortKey}
                  bind:sortOrder={sortDirection}
                  onSortChange={onSort}
                />
              </Col>
            {/if}
          {:else}
            <div class="mt-3">
              <NoMatches message={$format('label.MESSAGE.NO_MATCHES_CAMPAIGNS')} />
            </div>
          {/if}
        {:else if initialized}
          <Col>
            <EmptyState
              icon="calendar"
              heading={$format('label.EMPTY_STATE.CAMPAIGN_HEADING')}
              description={$format('label.EMPTY_STATE.CAMPAIGN_DESCRIPTION')}
              action={$format('label.EMPTY_STATE.CAMPAIGN_ACTION')}
              on:click={onNewCampaign}
            />
          </Col>
        {/if}
      </Row>
    </Container>
  </div>

  {#if queryResult.length && totalRecords}
    <div class="page-footer">
      <Pagination
        bind:currentPage={filters.paging.page}
        bind:perPage={filters.paging.perPage}
        bind:totalRecords
        {onChangeRecords}
        {onChangePage}
      />
    </div>
  {/if}
</div>

{#if creatingNewCampaign}
  <CampaignModal
    bind:show={showCampaignCreationModal}
    bind:showNextModal={showAdSetupModal}
    bind:campaignData={modalData}
    bind:tagFilters
  />
  <AdModal
    bind:show={showAdSetupModal}
    bind:showNextModal={showTargetingModal}
    bind:showPreviousModal={showCampaignCreationModal}
    bind:adData={modalData}
  />
  <TargetingModal
    bind:show={showTargetingModal}
    bind:showPreviousModal={showAdSetupModal}
    bind:targetingData={modalData}
  />
{/if}

<RenameModal
  bind:show={showRenameModal}
  data={renameCampaign}
  name={renameCampaign?.rulename ?? ''}
  existingNames={queryResult.map((c) => c.rulename)}
  duplicateError={$format('label.RENAME_MODAL.ERROR.DUPLICATE_CAMPAIGN')}
  onUpdate={onRenameUpdate}
/>

<svelte:window on:resize={onResize} />

<style lang="scss" src="./manage.scss"></style>
